import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { Observable, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { HttpService } from '../../../../shared/ajs-bridges/http.service';
import { BasicDataSubService } from '../../../../shared/basic-data-management/basic-data-subservice.class';
import { ImportService } from '../../../../shared/basic-data-management/import-service.class';

import { CachedDataSurveyService } from '../../../../technical-modules/tasks/services/cached-data-survey.service';

import { ParcTLR } from '../entities/parc-tlr.entity';
import { ParcWithGroupementDTO } from '../entities/parc-with-groupement-dto.entity';
import { UpdateParcOrder } from '../entities/update-parc-order.entity';
import { CreateParcOrder } from '../entities/create-parc-order.entity';
import { IndicateurCassini } from '../entities/indicateur-cassini.entity';
import { NotificationMeteo } from "../entities/notification-meteo.entity";
import { NotificationMeteoGroupee } from "../entities/notification-meteo-groupee.entity";

@Injectable({ providedIn: 'root' })
export class ParcService extends ImportService {
	private path: string;

	private parcTLRSubService!: BasicDataSubService<ParcWithGroupementDTO>;

	constructor( //
		@Inject(HttpClient) httpClient: HttpClient, //
		@Inject(HttpService) private httpService: HttpService, //
		@Inject(CachedDataSurveyService) cachedDataSurveyService: CachedDataSurveyService, //
		@Optional() @SkipSelf() previousInstance?: ParcService //
	) {
		super(
			httpClient,
			cachedDataSurveyService,
			"parcs",
			"Parc",
		);
		if (previousInstance) {
			throw new Error(
				'ParcService is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		let parcsChanged$ = this.cachedDataSurveyService.getChangeNotification("Parc");

		// NB: Depuis l'ajout des groupements de parc, le chargement d'une liste de parc avec leur indicatif de groupement
		// est devenu tres frequent, necessitant une mise en cache
		// Cependant, comme cette information n'est pas portee par le TLR, il faut charger a la place un DTO specifique
		// et puisque ce DTO porte toute les informations de base du TLR, charger les TLR en cache est donc faire deux fois
		// le travail necessaire
		//
		// On va donc mettre ces DTO en cache a la place du TLR, la compabilite avec les ancienne fonctions etant assurees
		// par le fait que la classe ParcWithGroupementDTO entend ParcTLR, aussi les fonctions n'ayant besoin que de la
		// partie TLR ne verront pas la difference
		//
		// Dans l'ideal, il faudrait que l'information de groupement soit portee par la table parc, pour ne pas avoir a
		// fair ce genre de choses
		// Il faudrait alors juste remettre la proprieter groupmentId dans le TLR et remplacemr toutes les references
		// du DO par des references au TLR. groupementParcChanged$ deviendrait aussi inutile ci-dessous
		let groupementParcChanged$ = this.cachedDataSurveyService.getChangeNotification("GroupementParc");
		let parcsWithGroupementsChanged$ = combineLatest([parcsChanged$, groupementParcChanged$]).pipe(
			map(
				timestamps => timestamps.reduce((date1, date2) => date1.getTime() < date2.getTime() ? date2 : date1)
			) 
		)
		this.parcTLRSubService = new BasicDataSubService<ParcWithGroupementDTO>(httpClient, this.path + 'parcs?format=withGroupement', parcsWithGroupementsChanged$);
	}

	public getParcsSelonProfilAndAntenne(profil: string, antenneId: number, profondeur: number): Promise<any> {
		if (profil === 'ADMIN' || profil === 'REFERENT') {
			return this.getParcs(profondeur);
		} else {
			return this.getParcsDeAntenne(antenneId, profondeur);
		}
	}

	public getParcsOptimisedSelonProfilAndAntenne(profil: string, antenneId: number, profondeur: number): Promise<any> {
		if (profil === 'ADMIN' || profil === 'REFERENT') {
			return this.getParcsOptimised(profondeur);
		} else {
			return this.getParcsOptimisedDeAntenne(antenneId, profondeur);
		}
	}

	public getSousParcsSelonProfilAndAntenne(profil: string, antenneId: number): Promise<any> {
		if (profil === 'ADMIN' || profil === 'REFERENT') {
			return this.getSousParcs();
		} else {
			return this.getSousParcsDeAntenne(antenneId);
		}
	}

	// Methode recuperant tous les parcs.
	public getParcs(profondeur?: number): Promise<any> {
		let parcsPath = this.path + 'parcs?format=DTO';

		if (profondeur && profondeur > 0) {
			parcsPath = parcsPath + '&p=' + profondeur;
		}
		let result = this.httpService.get(parcsPath);
		return result;
	}

	public getParcsOptimised(profondeur: number): Promise<any> {
		return this.getParcs(profondeur);
	}

	// Methode recuperant tous les parcs d'une antenne
	public getParcsDeAntenne(idAntenne: number, profondeur: number): Promise<any> {
		let parcsPath = this.path + 'parcs/idAntenne/' + idAntenne;
		if (profondeur > 0) {
			parcsPath = parcsPath + '?p=' + profondeur;
		}
		let result = this.httpService.get(parcsPath);
		return result;
	}

	// Methode recuperant tous les parcs d'une antenne
	public getParcsOptimisedDeAntenne(idAntenne: number, profondeur: number): Promise<any> {
		let parcsPath = this.path + 'parcs/optimised/idAntenne/' + idAntenne;
		if (profondeur > 0) {
			parcsPath = parcsPath + '?p=' + profondeur;
		}
		let result = this.httpService.get(parcsPath);
		return result;
	}


	public exportParcs(): Promise<any> {
		let url = this.path + 'parcs/export';
		return this.httpService.getAsArrayBuffer(url);
	}

	public exportSousParcs(): Promise<any> {
		let url = this.path + 'sousParcs/export';
		return this.httpService.getAsArrayBuffer(url);
	}

	// Methode permettant de recuperer un parc a partir de son identifiant.
	public getParcById(id: number): Promise<any> {
		let parcsPath = this.path + 'parcs/' + id;
		let result = this.httpService.get(parcsPath);
		return result;
	}

	// Methode permettant de supprimer un parc.
	public deleteParc(id: number): Promise<any> {
		let parcsPath = this.path + 'parcs/' + id;
		let result = this.httpService.delete(parcsPath);
		return result;
	}

	// Methode récupérant tous les sous-parcs.
	public getSousParcs(): Promise<any> {
		let sousParcsPath = this.path + 'sousParcs/';
		let result = this.httpService.get(sousParcsPath);
		return result;
	}

	// Methode récupérant tous les sous-parcs d'une antenne.
	public getSousParcsDeAntenne(idAntenne: number): Promise<any> {
		let sousParcsPath = this.path + 'sousParcs/idAntenne/' + idAntenne;
		let result = this.httpService.get(sousParcsPath);
		return result;
	}

	// Methode permettant d'ajouter un sous-parc.
	public createSousParc(data: any): Promise<any> {
		let url = this.path + 'sousParcs/';
		let result = this.httpService.post(url, data);
		return result;
	}

	// Methode permettant de modifier un sous-parc.
	public updateSousParc(id: number, data: any): Promise<any> {
		let url = this.path + 'sousParcs/' + id;
		let result = this.httpService.put(url, data);
		return result;
	}

	// Methode permettant de supprimer un sous-parc.
	public deleteSousParc(id: number): Promise<any> {
		let url = this.path + 'sousParcs/' + id;
		let result = this.httpService.delete(url);
		return result;
	}

	// Methode permettant de recuperer tous les parcs, leurs sous-parcs et ouvrages.
	public getParcsFull(): Promise<any> {
		let parcsPath = this.path + 'parcs?p=1';
		let result = this.httpService.get(parcsPath);
		return result;
	}

	// Methode permettant de savoir si un parc a des acces en cours.
	public parcHasAccesEnCours(id: number): Promise<any> {
		return this.httpService.get(this.path + 'parcs/hasAccesEnCours/' + id);
	}

	// Methode permettant de savoir si un sous-parc a des acces en cours.
	public sousParcHasAccesEnCours(id: number): Promise<any> {
		return this.httpService.get(this.path + 'sousParcs/hasAccesEnCours/' + id);
	}

	// MEthode permettant de recuperer l'id de l'ouvrage SOL attache a un parc solaire
	public getSolParcOuvrageId(parcId: number): Promise<any> {
		let url = this.path + 'parcs/solaire/' + parcId + '/ouvrage-parc/id';
		return this.httpService.get(url);
	}

	///////////////////////////////////////////
	// Nouvelles fonctions en pur Angular 12 //
	///////////////////////////////////////////

	// Methode permettant le chargement de tous les parcs
	public getParcTLRList = () => this.parcTLRSubService.getList();

	// Methodes permettant le chargement de tous les parcs, selon differentes classifications et indexs
	// (NB: on procede a toutes les classifications en une fois, ce qui permet de n'iterer qu'une seule fois pour tous les resultats)
	public getParcTLRMapById = () => this.parcTLRSubService.getMapById();
	public getParcTLRById = (id: number) => this.parcTLRSubService.getKnownById(id, "ParcTLR");
	public getParcTLRMapByAntenne = () => this.parcTLRSubService.getMapByForeignKey("byAntenne", (parc: ParcTLR) => parc.antenneId);
	public getParcTLRByAntenne = (id: number) => this.parcTLRSubService.getByForeignKey("byAntenne", (parc: ParcTLR) => parc.antenneId, id);
	public getParcTLRMapByGroupement = () => this.parcTLRSubService.getMapByForeignKey("byGroupement", (parc: ParcWithGroupementDTO) => parc.groupementId);
	public getParcTLRByGroupement = (id: number) => this.parcTLRSubService.getByForeignKey("byGroupement", (parc: ParcWithGroupementDTO) => parc.groupementId, id);
	 
	// Recupere tous les identtifiants des parcs pour lequel un utilisateur a un moins un ouvrage pour lequel il est CAC
	// NB: Cette fonction ne gere pas de cache, car les CAC peuvent changer a chaque instant de l'application
	public getParcIdsForCac(cacId: number): Observable<number[]> {
		let url = this.path + 'parcs?format=ID&cac=' + cacId;
		return this.httpClient.get<number[]>(url);
	}

	// Nouvelle methode de creation d'un parc
	public create(command: CreateParcOrder) : Observable<number> {
		let url = this.path + 'parcs/create-orders';
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$.pipe(
			tap(() => this.parcTLRSubService.clearCaches())
		);		
	}

	// Methode de mise a jour d'un parc avancee
	// cf Evol 53610
	public updateWithCac(command: UpdateParcOrder) : Observable<number> {
		let url = this.path + 'parcs/update-orders';
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$.pipe(
			tap(() => this.parcTLRSubService.clearCaches())
		);		
	}
	
	public getEnergieAsIcon(parc: ParcTLR) {
		let energie = parc.energie;
		let icon = '';
		switch (energie) {
			case 'Solaire':
				icon = 'fa-th';
				break;
			case 'Eolienne':
				icon = 'fa-brands fa-yelp';
				break;
			case 'Stockage':
				icon = 'fa-regular fa-floppy-disk';
				break;
			default:
				icon = 'fa-genderless';
			break;
		}
		return icon;
	}
	
	public clearCaches() {
		this.parcTLRSubService.clearCaches();
	}
	
	public getParcIndicateurs(parcId: number): Observable<IndicateurCassini[]> {
		let url = this.path + 'parcs/indicateurs/' + parcId;
		return this.httpClient.get<IndicateurCassini[]>(url);
	}
	
	public getMeteoNotifEvents(userId: number): Observable<Record<number, NotificationMeteoGroupee>> {
		let url = this.path + 'acces/meteo-notifs/' + userId;
		return this.httpClient.get<Record<number, NotificationMeteoGroupee>>(url);
	}

	public markMeteoNotifsAsViewed(notifIds: number[]): Observable<Record<number, NotificationMeteoGroupee>> {
		let url = this.path + 'acces/notifs/meteo/viewed';
		return this.httpClient.put<Record<number, NotificationMeteoGroupee>>(url, notifIds);
	}
}